import { Editor, Node, Path, Transforms } from 'slate';

import * as serializer from 'basic-editor-value-serializer';

import { wrapInLayout } from './wrap';

const types = serializer.types;
const layoutUtilities = serializer.utilities.node.layout;

function isSource(sourceReferences, path) {

  for (let index = 0; index < sourceReferences.length; index++) {
    if (Path.equals(sourceReferences[index].current, path) === true) {
      return true;
    }
  }

  return false;

}

function findAnchor(editor, sourceReferences, parentPath, index) {

  let anchorIndex = index;

  while (Node.has(editor, parentPath.concat(anchorIndex)) === true && isSource(sourceReferences, parentPath.concat(anchorIndex)) === true) {
    anchorIndex++;
  }

  if (Node.has(editor, parentPath.concat(anchorIndex)) === false) {
    return null;
  }

  return Editor.pathRef(editor, parentPath.concat(anchorIndex));

}

// moveNodes takes the final index under the parent's current path, so a block moving forward
// among its siblings lands one index earlier.
function findMoveTarget(editor, from, anchorReference, parentPath) {

  if (anchorReference !== null) {
    if (Path.isSibling(from, anchorReference.current) === true && Path.isBefore(from, anchorReference.current) === true) {
      return Path.previous(anchorReference.current);
    }
    return anchorReference.current;
  }

  const count = Node.get(editor, parentPath).children.length;

  if (Path.isChild(from, parentPath) === true) {
    return parentPath.concat(count - 1);
  }

  return parentPath.concat(count);

}

function moveSources(editor, sourceReferences, parentPath, index) {

  const parentReference = Editor.pathRef(editor, parentPath);
  const anchorReference = findAnchor(editor, sourceReferences, parentPath, index);

  for (let sourceIndex = 0; sourceIndex < sourceReferences.length; sourceIndex++) {

    const from = sourceReferences[sourceIndex].current;
    const to = findMoveTarget(editor, from, anchorReference, parentReference.current);

    if (Path.equals(from, to) === false) {
      Transforms.moveNodes(editor, { at: from, to });
    }

  }

  parentReference.unref();

  if (anchorReference !== null) {
    anchorReference.unref();
  }

}

// A drop plan is { kind: 'move', parentPath, index, targetPath } or
// { kind: 'wrap', targetPath, direction: 'row' | 'column', side: 'start' | 'end' } (spec 3.9).
function wrapAndMove(editor, sourceReferences, plan) {

  let layoutNode = layoutUtilities.buildStackNode([serializer.utilities.node.paragraph.buildNode()]);

  if (plan.direction === 'row') {
    layoutNode = layoutUtilities.buildNode({ children: [serializer.utilities.node.paragraph.buildNode()] });
  }

  const targetPath = wrapInLayout(editor, plan.targetPath, layoutNode);

  if (plan.side === 'start') {
    moveSources(editor, sourceReferences, Path.parent(targetPath), 0);
    return;
  }

  moveSources(editor, sourceReferences, Path.parent(targetPath), 1);

}

// A layout a dropped block left empty is removed, and so is any layout that leaves empty.
function removeEmptyLayouts(editor, parentReference) {

  let path = parentReference.unref();

  while (path !== null && path.length > 0 && Node.get(editor, path).type === types.LAYOUT && Node.get(editor, path).children.length === 0) {
    Transforms.removeNodes(editor, { at: path });
    path = Path.parent(path);
  }

}

function createReferences(editor, paths, levelsUp) {

  const references = [];

  for (let index = 0; index < paths.length; index++) {
    references.push(Editor.pathRef(editor, paths[index].slice(0, paths[index].length - levelsUp)));
  }

  return references;

}

// Moves the dragged blocks, in document order, as the plan says, in one undo step.
export function applyDrop(editor, sourcePaths, plan) {

  const orderedPaths = sourcePaths.slice().sort(Path.compare);
  const sourceReferences = createReferences(editor, orderedPaths, 0);
  const parentReferences = createReferences(editor, orderedPaths, 1);

  Editor.withoutNormalizing(editor, function drop() {

    if (plan.kind === 'wrap') {
      wrapAndMove(editor, sourceReferences, plan);
    } else {
      moveSources(editor, sourceReferences, plan.parentPath, plan.index);
    }

    for (let index = 0; index < parentReferences.length; index++) {
      sourceReferences[index].unref();
      removeEmptyLayouts(editor, parentReferences[index]);
    }

  });

}
